import { useEffect, useRef, type RefObject } from 'react';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from '../../design-system/components/DataTable';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { HelpNote } from '../../help';
import type { OlderTask } from './older-tasks';

/**
 * The Tasks tab's "Older tasks": finished jobs that have left the recent
 * list, read from the stored record a page at a time.
 *
 * It REPLACES the recent table rather than sitting under it. The recent
 * table fills the screen and scrolls inside itself (`nx-page-fill`) so
 * its header stays put; a second table below it would have to share that
 * height, and on a phone -- where the new-job form already takes half the
 * screen -- both would be a few rows tall. One table at a time, with a
 * clearly named way back, works at every width.
 *
 * Presentational: the screen owns the read, the layout and the filters,
 * because the filter row and the Columns control serve both lists.
 */
export function OlderTasksSection({
  rows,
  loadedCount,
  columns,
  defaultSort,
  isLoading,
  isError,
  isFetchNextPageError,
  hasNextPage,
  isFetchingNextPage,
  gapError,
  gapPartial,
  onRetryGap,
  onLoadMore,
  onRetry,
  onOpen,
  onBack,
  headingRef,
}: {
  /** Loaded, de-duplicated against the live list, and filtered. */
  rows: OlderTask[];
  /** Loaded and de-duplicated, before the filters. */
  loadedCount: number;
  columns: DataTableColumn<OlderTask>[];
  defaultSort: DataTableSort;
  /** The first page is on its way. */
  isLoading: boolean;
  /** The FIRST page failed: there is nothing to show. */
  isError: boolean;
  /** A later page failed: what is shown stays, the button retries. */
  isFetchNextPageError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** The tasks that left the recent list after this view opened could
   *  not be read (`useOlderTasksGap`). What is loaded stays. */
  gapError: boolean;
  /** More left the recent list than that read brings back. */
  gapPartial: boolean;
  onRetryGap: () => void;
  onLoadMore: () => void;
  onRetry: () => void;
  onOpen: (task: OlderTask) => void;
  onBack: () => void;
  /** The screen moves focus here when someone opens this view. */
  headingRef?: RefObject<HTMLHeadingElement>;
}) {
  // THE PAGER KEEPS FOCUS, AND HANDS IT ON WHEN IT GOES. It is
  // aria-disabled while a page loads, not disabled -- a disabled button
  // drops focus, as the Columns control's arrows explain -- and on the
  // last page it is replaced by the end note. A press that found it
  // focused is remembered, so that when the button is gone the note
  // takes focus instead of the page body, from which the next Tab
  // starts again at the side nav.
  const endRef = useRef<HTMLParagraphElement>(null);
  const pagerHadFocus = useRef(false);
  useEffect(() => {
    if (isFetchingNextPage || !pagerHadFocus.current) return;
    pagerHadFocus.current = false;
    if (!hasNextPage) endRef.current?.focus();
  }, [isFetchingNextPage, hasNextPage]);

  let body;
  if (isLoading) {
    body = <SkeletonLoader shape="table-row" count={3} />;
  } else if (isError) {
    body = (
      <div data-testid="tasks-older-error">
        <AlertBanner
          variant="error"
          title="Older tasks could not be loaded"
          actions={
            <button
              type="button"
              className="nx-tasks__cta"
              onClick={onRetry}
              data-testid="tasks-older-retry"
            >
              Retry
            </button>
          }
        >
          Press Retry. If it still fails, call Nexsoss.
        </AlertBanner>
      </div>
    );
  } else if (loadedCount === 0) {
    body = (
      <div data-testid="tasks-older-empty">
        {hasNextPage ? (
          <AlertBanner variant="info" title="Nothing older yet">
            Load older tasks to look further back.
          </AlertBanner>
        ) : (
          <AlertBanner variant="info" title="No older tasks">
            Every finished job on record is in the recent list.
          </AlertBanner>
        )}
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div data-testid="tasks-older-filtered">
        <AlertBanner variant="info" title="No older tasks match the current filters">
          {hasNextPage
            ? 'Clear the filters, or load older tasks to look further back.'
            : 'Clear the filters to see them all.'}
        </AlertBanner>
      </div>
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={rows}
        getRowId={(t) => t.id}
        ariaLabel="Older tasks"
        onRowClick={onOpen}
        defaultSort={defaultSort}
      />
    );
  }

  const showFoot = !isLoading && !isError;
  return (
    <section
      className="nx-tasks-older"
      aria-labelledby="tasks-older-title"
      data-testid="tasks-older"
    >
      <div className="nx-tasks-older__head">
        <div className="nx-tasks-older__heading">
          <h2
            id="tasks-older-title"
            className="nx-tasks-older__title"
            ref={headingRef}
            tabIndex={-1}
          >
            Older tasks
          </h2>
          <p className="nx-tasks-older__lede">
            Finished jobs that have left the recent list, the latest to finish first.
          </p>
        </div>
        <button
          type="button"
          className="nx-tasks-older__switch"
          onClick={onBack}
          data-testid="tasks-older-back"
        >
          Back to recent tasks
        </button>
      </div>
      <HelpNote id="tasks.older" />
      {gapError && (
        <p className="nx-tasks-older__error" role="alert" data-testid="tasks-older-gap-error">
          Jobs that left the recent list since you opened this could not be loaded.{' '}
          <button type="button" className="nx-tasks-older__inline" onClick={onRetryGap}>
            Retry
          </button>
        </p>
      )}
      {!gapError && gapPartial && (
        <p className="nx-tasks-older__note" data-testid="tasks-older-gap-partial">
          Some jobs that left the recent list since you opened this are not shown. Go back to
          recent tasks and open Older tasks again to see them.
        </p>
      )}
      {body}
      {showFoot && (
        <div className="nx-tasks-older__foot">
          {isFetchNextPageError && (
            <p className="nx-tasks-older__error" role="alert" data-testid="tasks-older-more-error">
              Could not load more. Press Load older tasks to try again.
            </p>
          )}
          {hasNextPage ? (
            <button
              type="button"
              className="nx-tasks-older__more"
              onClick={(e) => {
                if (isFetchingNextPage) return;
                pagerHadFocus.current = document.activeElement === e.currentTarget;
                onLoadMore();
              }}
              aria-disabled={isFetchingNextPage}
              data-testid="tasks-older-load"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load older tasks'}
            </button>
          ) : (
            loadedCount > 0 && (
              <p
                className="nx-tasks-older__end"
                ref={endRef}
                tabIndex={-1}
                data-testid="tasks-older-end"
              >
                That is everything on record.
              </p>
            )
          )}
        </div>
      )}
      {/* Said, not shown: a screen reader hears each page arrive, which
          the table growing below the fold does not tell it. */}
      <p className="nx-tasks-older__status" role="status" data-testid="tasks-older-status">
        {showFoot ? `${loadedCount} older ${loadedCount === 1 ? 'task' : 'tasks'} loaded.` : ''}
      </p>
    </section>
  );
}
